<script setup lang="ts">
import {reactive, ref, watchEffect} from "vue";
import {Avatar, Button, Dialog, Drawer, Label, InputText} from "@/Components/Library";
import {useMainStore} from "@/Stores/store";
import {getGroupMemberStats} from "@/Services/group-members";
import {getting} from "@/Composables/api";
import {PageLoading} from "@/Components/Global";
import User from "@/Models/User";
import {storeContact} from "@/Services/contacts.ts";
import {useToast} from "primevue/usetoast";
import {storeConversation} from "@/Services/conversations";
import {useRouter} from "vue-router";

const $store = useMainStore();
const $user = new User();
const toast = useToast();
const router = useRouter();

const props = defineProps<{
    group?: any;
    member: any;
}>();
const model = defineModel();

const state = reactive({
    show: {
        conversationDialog: false,
    },
    loading: {
        conversation: false,
        contact: false,
    },
});

const stats = ref<{
    comments: number;
    messages: number;
    likes: number;
    responses: number;
    your_responses: number;
    this_member_responses: number;
    number_of_questions_responded: number;
    total_questions: number;
    total_responses: number;
    is_contact: boolean;
} | null>(null);
const conversationTitle = ref(null);

const addContact = async () => {
    state.loading.contact = true;
    try {
        await storeContact({group_id: props.group.id, member_id: props.member.id});

        if (stats.value) {
            stats.value.is_contact = true;
        }

        toast.add({
            severity: "success",
            summary: "Contact added",
            detail: `${props.member.first_name} ${props.member.last_name} has been added to your contacts.`,
            life: 3000,
        });
    } catch (e) {
        console.log(e);
    } finally {
        state.loading.contact = false;
    }
};

const startConversation = async () => {
    state.loading.conversation = true;
    try {
        const {data} = await storeConversation({
            title: conversationTitle.value,
            participants: [props.member.id],
            tags: [],
            add_contacts: !stats.value?.is_contact,
        });

        toast.add({
            severity: "success",
            summary: "Chat started",
            detail: `Chat with ${props.member.first_name} ${props.member.last_name} has been created.`,
            life: 3000,
        });

        model.value = false;
        await router.push({name: "conversations.show", params: {conversation: data.id}});
    } catch (e) {
        console.log(e);
    } finally {
        state.loading.conversation = false;
    }
};

watchEffect(() => {
    if (props.member) {
        getGroupMemberStats(props.group.id, props.member.id).then((data) => {
            stats.value = data;
        });
    }
});
</script>
<template>
    <Drawer
        v-model:visible="model"
        header=" "
        position="bottom"
        class="relative max-h-screen max-w-3xl overflow-y-auto"
    >
        <div v-auto-animate>
            <div v-if="getting" class="py-8">
                <PageLoading />
            </div>
            <div v-else class="flex flex-col gap-6 pb-12">
                <div class="flex flex-col items-center gap-4">
                    <Avatar :user="member" class="h-16 w-16 text-3xl" shape="circle" />
                    <h2 class="text-xl font-bold sm:text-3xl">{{ member.first_name }} {{ member.last_name }}</h2>
                </div>
                <div v-if="$user.id !== member.id && group.anonymization.slug === 'full_name'" class="w-full px-4">
                    <Button
                        class="w-full bg-askr-green text-white hover:brightness-110"
                        @click="addContact"
                        :disabled="stats?.is_contact"
                        :loading="state.loading.contact"
                    >
                        {{ stats?.is_contact ? "Already a contact" : "Add as a contact" }}
                    </Button>
                </div>
                <div
                    v-if="
                        $user.id !== member.id &&
                        (member.id === group.user.id || group.anonymization.slug === 'full_name')
                    "
                    class="w-full px-4"
                >
                    <Button
                        class="w-full bg-askr-green text-white hover:brightness-110"
                        @click="state.show.conversationDialog = true"
                    >
                        Start a conversation
                    </Button>
                </div>
                <div class="flex flex-col gap-6">
                    <div class="rounded-lg bg-stone-50 p-4 dark:bg-stone-900">
                        <h3 class="mb-2 text-sm">Stats</h3>
                        <div v-if="stats" class="flex flex-col gap-4">
                            <div>
                                <span class="font-bold">{{ stats.number_of_questions_responded }}</span>
                                questions responded out of
                                <span class="font-bold">{{ stats.total_questions }}</span>
                            </div>
                            <div>
                                This member contributed
                                <span class="font-bold">{{ stats.this_member_responses }}</span>
                                responses out of
                                <span class="font-bold">{{ stats.total_responses }}</span>
                                total responses in this group.
                            </div>
                            <div v-if="$store.user.id !== member.id">
                                <span v-if="stats.this_member_responses === stats.your_responses">
                                    You have the same number of responses as this member.
                                </span>
                                <span v-else-if="stats.this_member_responses > stats.your_responses">
                                    You have
                                    <span class="font-bold">
                                        {{ stats.your_responses - stats.this_member_responses }}
                                    </span>
                                    less responses than this member.
                                </span>
                                <span v-else>
                                    You have
                                    <span class="font-bold">
                                        {{ stats.your_responses - stats.this_member_responses }}
                                    </span>
                                    more responses than this member.
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Dialog
            modal
            dismissable-mask
            header="Start chat"
            v-model:visible="state.show.conversationDialog"
            class="w-full max-w-xl"
            @hide="conversationTitle = null"
        >
            <p class="mb-2">
                {{
                    `${member.first_name} ${member.last_name} will be added ${!stats?.is_contact ? "to contacts and " : ""}to a new chat with you.`
                }}
            </p>
            <form @submit.prevent="startConversation">
                <div class="flex flex-col">
                    <div class="mb-6 flex flex-col">
                        <Label for="title" class="mb-1" required>Chat title</Label>
                        <InputText v-model="conversationTitle" id="title" name="title" fluid required />
                    </div>
                    <Button
                        type="submit"
                        class="mb-2 w-full bg-askr-green text-white hover:brightness-110"
                        :loading="state.loading.conversation"
                    >
                        Create
                    </Button>
                    <Button
                        type="button"
                        class="w-full text-sm !font-normal"
                        @click="state.show.conversationDialog = false"
                        :disabled="state.loading.conversation"
                    >
                        Cancel
                    </Button>
                </div>
            </form>
        </Dialog>
    </Drawer>
</template>
<style scoped></style>
